import { useMemo } from "react";

export type ChartSeries = {
  channelId: string;
  points: Array<{ date: string; count: number }>;
};

const SERIES_COLORS = ["#3b82f6", "#22c55e", "#eab308", "#a855f7", "#ef4444", "#06b6d4"];

type Props = {
  series: ChartSeries[];
  height?: number;
};

function formatTick(date: string): string {
  // YYYY-MM-DD → MM-DD
  const parts = date.split("-");
  if (parts.length === 3) return `${parts[1]}-${parts[2]}`;
  return date;
}

export default function CashflowLineChart({ series, height = 320 }: Props) {
  const { dates, paths, ticks } = useMemo(() => {
    const dates = series[0]?.points.map((p) => p.date) ?? [];
    let maxY = 1;
    for (const s of series) {
      for (const p of s.points) {
        if (p.count > maxY) maxY = p.count;
      }
    }
    // nice upper bound
    maxY = Math.ceil(maxY * 1.1) || 1;

    const padL = 40;
    const padR = 16;
    const padT = 16;
    const padB = 36;
    const w = 900;
    const h = height;
    const plotW = w - padL - padR;
    const plotH = h - padT - padB;
    const n = Math.max(dates.length - 1, 1);

    const xAt = (i: number) => padL + (i / n) * plotW;
    const yAt = (v: number) => padT + plotH - (v / maxY) * plotH;

    const paths = series.map((s, si) => {
      const d = s.points
        .map((p, i) => `${i === 0 ? "M" : "L"} ${xAt(i).toFixed(1)} ${yAt(p.count).toFixed(1)}`)
        .join(" ");
      return { channelId: s.channelId, d, color: SERIES_COLORS[si % SERIES_COLORS.length] };
    });

    const yTickCount = 5;
    const yTicks = Array.from({ length: yTickCount + 1 }, (_, i) => {
      const v = (maxY / yTickCount) * i;
      return { v, y: yAt(v) };
    });

    const xLabelStep = Math.max(1, Math.floor(dates.length / 10));
    const xTicks = dates
      .map((date, i) => ({ date, i, x: xAt(i) }))
      .filter((_, i) => i % xLabelStep === 0 || i === dates.length - 1);

    return {
      dates,
      paths,
      ticks: { yTicks, xTicks, w, h, padL, padT, plotW, plotH },
    };
  }, [series, height]);

  if (!dates.length) {
    return <div className="cashflow-chart__empty">暂无数据</div>;
  }

  const { yTicks, xTicks, w, h, padL, padT, plotW, plotH } = ticks;

  return (
    <div className="cashflow-chart">
      <svg viewBox={`0 0 ${w} ${h}`} className="cashflow-chart__svg" role="img" aria-label="近30天交易量">
        {/* grid */}
        {yTicks.map((t) => (
          <g key={t.v}>
            <line
              x1={padL}
              y1={t.y}
              x2={padL + plotW}
              y2={t.y}
              stroke="#e5e7eb"
              strokeWidth={1}
            />
            <text x={padL - 8} y={t.y + 4} textAnchor="end" className="cashflow-chart__tick">
              {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
            </text>
          </g>
        ))}
        {xTicks.map((t) => (
          <text key={t.date} x={t.x} y={padT + plotH + 20} textAnchor="middle" className="cashflow-chart__tick">
            {formatTick(t.date)}
          </text>
        ))}
        {paths.map((p) => (
          <path key={p.channelId} d={p.d} fill="none" stroke={p.color} strokeWidth={2.5} strokeLinejoin="round" />
        ))}
      </svg>
      <div className="cashflow-chart__legend">
        {paths.map((p) => (
          <span key={p.channelId} className="cashflow-chart__legend-item">
            <i style={{ background: p.color }} />
            {p.channelId}
          </span>
        ))}
      </div>
    </div>
  );
}
